import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  User,
  Buildings as Building,
  Flask,
  Eye,
  Briefcase,
} from '@phosphor-icons/react';
import { useContactsStore } from '@/lib/contactsStore';
import { useAuthStore } from '@/features/auth/store';
import { canMutateContacts } from '@/lib/pupilUiBoundary';
import { EmptyState } from '@/components/shared/EmptyState';
import { isValidName, isValidPhone, isValidEmail, sanitizeName, NAME_ERROR, PHONE_ERROR, EMAIL_ERROR } from '@/lib/validators';
import { PhoneInput } from '@/components/ui/phone-input';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';

type ContactType = 'lay' | 'defendant' | 'solicitor' | 'expert' | 'witness' | 'company';

const TYPES: { key: ContactType; label: string; icon: typeof User }[] = [
  { key: 'lay', label: 'Lay client', icon: User },
  { key: 'defendant', label: 'Defendant', icon: User },
  { key: 'solicitor', label: 'Instructing solicitor', icon: Briefcase },
  { key: 'expert', label: 'Expert', icon: Flask },
  { key: 'witness', label: 'Witness', icon: Eye },
  { key: 'company', label: 'Company', icon: Building },
];

const PREFIXES = ['Mr', 'Mrs', 'Ms', 'Miss', 'Dr', 'Prof', 'QC', 'KC', 'Lord', 'Lady', 'The Honourable'];
const showOrg = (t: ContactType) => t === 'solicitor' || t === 'expert' || t === 'company';

export default function NewContactPage() {
  const navigate = useNavigate();
  const addContact = useContactsStore((s) => s.addContact);
  const fetchContacts = useContactsStore((s) => s.fetchContacts);
  const memberType = useAuthStore((s) => s.user?.memberType);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const [type, setType] = useState<ContactType>('lay');
  const [prefix, setPrefix] = useState('');
  const [first, setFirst] = useState('');
  const [middle, setMiddle] = useState('');
  const [last, setLast] = useState('');
  const [dob, setDob] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [sra, setSra] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [website, setWebsite] = useState('');
  const [addr1, setAddr1] = useState('');
  const [addr2, setAddr2] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Progressive disclosure — Address and Notes are wholly optional and ran the form past
  // 1.9 screens. Each forces itself open the moment it holds anything, so nothing a user
  // typed can end up hidden behind a collapsed heading.
  const [showAddress, setShowAddress] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const addressOpen =
    showAddress ||
    !!addr1.trim() || !!addr2.trim() || !!city.trim() || !!county.trim() || !!postcode.trim();
  const notesOpen = showNotes || !!tags.trim() || !!notes.trim();

  const displayName = [prefix, first, middle, last].filter(Boolean).join(' ') || 'New Contact';
  const initials = [first, last].filter(Boolean).map((n) => n[0]).join('').slice(0, 2) || '?';

  // Field-level validation. Required (name/email/phone) errors surface after a
  // submit attempt; format errors (digits in a name, bad email) surface live.
  const firstError  = first.trim()  ? (isValidName(first) ? '' : NAME_ERROR)   : (submitted ? 'First name is required.' : '');
  const middleError = middle.trim() && !isValidName(middle) ? NAME_ERROR : '';
  const lastError   = last.trim()   ? (isValidName(last) ? '' : NAME_ERROR)    : (submitted ? 'Last name is required.' : '');
  const emailError  = email.trim()  ? (isValidEmail(email) ? '' : EMAIL_ERROR) : (submitted ? 'Email is required.' : '');
  const phoneError  = phone.trim()  ? (isValidPhone(phone) ? '' : PHONE_ERROR) : (submitted ? 'Phone is required.' : '');
  const mobileError = mobile.trim() && !isValidPhone(mobile) ? PHONE_ERROR : '';

  // Hard requirements regardless of submit state — used to block creation.
  const hasError =
    !first.trim() || !isValidName(first) ||
    !last.trim()  || !isValidName(last) ||
    !email.trim() || !isValidEmail(email) ||
    !phone.trim() || !isValidPhone(phone) ||
    !!middleError || !!mobileError;

  const handleCreate = async () => {
    setSubmitted(true);
    if (hasError) { toast.error('Please fix the highlighted required fields.'); return; }
    try {
      await addContact({
        type,
        name: `${first} ${last}`.trim(),
        org: company || '—',
        email,
        phone: phone || mobile || '',
        cases: 0,
        kyc: '—',
        tags: tags ? tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
        notes,
      });
      toast.success(`Contact "${first} ${last}" created`);
      navigate(-1);
    } catch {
      toast.error('Could not create contact — please try again.');
    }
  };

  const Label = ({ children, required }: { children: string; required?: boolean }) => (
    <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
      {children}{required && <span className="text-danger"> *</span>}
    </label>
  );

  const Err = ({ msg }: { msg: string }) => (msg ? <p className="mt-1 text-xs text-danger">{msg}</p> : null);

  // CONTACTS ARE READ-ONLY FOR A PUPIL — no `contact.create`, so `POST /contacts` answers 403.
  // "New client" is already withheld on the list (MyClientsPage) and Edit/Delete on the detail page,
  // but `/*/clients/new` and `/*/contacts/new` stayed reachable directly: the form opened, took
  // every field, validated them, and then failed on submit with "please try again" — a refusal
  // offered as a retryable fault, which is the defect the Reports page fix named. (The store's
  // `addContact` does await and rethrow, so the row is rolled back and nothing is falsely
  // confirmed; the dead-end is the form itself.) Withhold the form instead.
  //
  // After every hook, so hook order does not vary by role. The server still decides.
  if (!canMutateContacts(memberType)) {
    return (
      <div>
        <PageHeader title="New Contact" onBack={() => navigate(-1)} />
        <EmptyState
          icon={<User className="h-6 w-6" />}
          title="Adding a client isn't available to your role"
          description="Client and contact records are maintained by the tenants and clerks. Every contact in chambers is yours to read."
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="New Contact"
        onBack={() => navigate(-1)}
      />

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        {/* ── LEFT: Form cards ── */}
        <div className="space-y-5">
          {/* Personal Details */}
          <Card>
            <CardHeader><CardTitle>Personal Details</CardTitle></CardHeader>
            <CardContent>
              {/* Contact type — an even grid, not a wrapping row. Six labels of different
                  lengths wrapped 2-2-2 on a phone with ragged right edges; equal tiles read
                  as one control. Who the contact IS and what they are called belong together. */}
              <div className="mb-4">
                <Label>Contact type</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.key}
                        onClick={() => setType(t.key)}
                        className={`flex h-full w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-center text-xs font-medium leading-tight transition ${
                          type === t.key
                            ? 'border-brand-600 bg-brand-50 text-brand-700 dark:text-brand-300'
                            : 'border-border bg-card text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />{t.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* 3-up on a wide screen: at two columns these five fields took three rows,
                  one of them half empty because Prefix sat beside a spacer. */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <Label>Title / Prefix</Label>
                  <select value={prefix} onChange={(e) => setPrefix(e.target.value)} className="flex h-9 w-full rounded-lg border border-border/70 bg-card px-3 text-sm text-foreground focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100/50">
                    <option value="">None</option>
                    {PREFIXES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div><Label required>First name</Label><Input value={first} onChange={(e) => setFirst(sanitizeName(e.target.value))} placeholder="e.g. Catherine" maxLength={200} /><Err msg={firstError} /></div>
                <div><Label>Middle name</Label><Input value={middle} onChange={(e) => setMiddle(sanitizeName(e.target.value))} maxLength={200} /><Err msg={middleError} /></div>
                <div><Label required>Last name</Label><Input value={last} onChange={(e) => setLast(sanitizeName(e.target.value))} placeholder="e.g. Hughes" maxLength={200} /><Err msg={lastError} /></div>
                <div><Label>Date of birth</Label><Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} /></div>
              </div>
            </CardContent>
          </Card>

          {/* Organisation (conditional) */}
          {showOrg(type) && (
            <Card>
              <CardHeader><CardTitle>Organisation</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div><Label>Company / Firm name</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} /></div>
                  <div><Label>Job title</Label><Input value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} /></div>
                  {type === 'solicitor' && (
                    <div><Label>SRA number</Label><Input value={sra} onChange={(e) => setSra(e.target.value)} placeholder="e.g. 123456" /></div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Information */}
          <Card>
            <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div><Label required>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" maxLength={255} /><Err msg={emailError} /></div>
                <div><Label required>Phone</Label><PhoneInput value={phone} onChange={setPhone} error={phoneError || undefined} /></div>
                <div><Label>Mobile</Label><PhoneInput value={mobile} onChange={setMobile} error={mobileError || undefined} /></div>
                <div><Label>Website</Label><Input type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://" /></div>
              </div>
            </CardContent>
          </Card>

          {/* Address */}
          <Card>
            <CardHeader>
              <button
                type="button"
                onClick={() => setShowAddress(!addressOpen)}
                aria-expanded={addressOpen}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <CardTitle>
                  Address{!addressOpen && <span className="ml-2 text-xs font-normal text-muted-foreground">(optional)</span>}
                </CardTitle>
                <span className="text-xs font-medium text-brand-600 dark:text-brand-400">{addressOpen ? 'Hide' : 'Add'}</span>
              </button>
            </CardHeader>
            {addressOpen && (
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>Address line 1</Label><Input value={addr1} onChange={(e) => setAddr1(e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Address line 2</Label><Input value={addr2} onChange={(e) => setAddr2(e.target.value)} /></div>
                <div><Label>City</Label><Input value={city} onChange={(e) => setCity(e.target.value)} /></div>
                <div><Label>County</Label><Input value={county} onChange={(e) => setCounty(e.target.value)} /></div>
                <div><Label>Postcode</Label><Input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="SW1A 1AA" /></div>
              </div>
            </CardContent>
            )}
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <button
                type="button"
                onClick={() => setShowNotes(!notesOpen)}
                aria-expanded={notesOpen}
                className="flex w-full items-center justify-between gap-2 text-left"
              >
                <CardTitle>
                  Notes{!notesOpen && <span className="ml-2 text-xs font-normal text-muted-foreground">(optional)</span>}
                </CardTitle>
                <span className="text-xs font-medium text-brand-600 dark:text-brand-400">{notesOpen ? 'Hide' : 'Add'}</span>
              </button>
            </CardHeader>
            {notesOpen && (
            <CardContent>
              <div className="space-y-4">
                <div><Label>Tags</Label><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Criminal, Family, Repeat instructor (comma separated)" /></div>
                <div>
                  <Label>Notes</Label>
                  <ExpandableTextarea label="Notes" rows={4} value={notes} onChange={setNotes} placeholder="Any additional notes about this contact..." />
                </div>
              </div>
            </CardContent>
            )}
          </Card>
        </div>

        {/* ── RIGHT: Sidebar ── */}
        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardContent className="flex flex-col items-center gap-3 pt-6 text-center">
              <Avatar fallback={initials} size="xl" />
              <div>
                <p className="text-sm font-semibold text-foreground">{displayName}</p>
                <Badge className="mt-1">{TYPES.find((t) => t.key === type)?.label}</Badge>
              </div>
              {email && <p className="text-xs text-muted-foreground">{email}</p>}
            </CardContent>
          </Card>

          <Button className="w-full" size="lg" onClick={handleCreate}>Create contact</Button>
        </div>
      </div>
    </div>
  );
}
